<template>
  <div class="tfb-page" ref="tfbPageRoot" :style="pageStyle">
    <!-- ===== Toolbar ===== -->
    <v-toolbar color="primary" density="comfortable" class="text-white">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" data-testid="tfb-back" @click="goBack">
        รายการสูตร
      </v-btn>
      <v-toolbar-title class="tfb-title">
        {{ isNewFormula ? 'สูตรใหม่' : 'แก้ไขสูตร' }}
        <code v-if="meta.outputVar" class="tfb-ov">{{ meta.outputVar }}</code>
        <span v-if="versionCode" class="tfb-sub">· {{ versionCode }}</span>
      </v-toolbar-title>
      <v-spacer />

      <v-chip v-if="dirty" size="small" color="warning" variant="tonal" class="mr-2" data-testid="tfb-unsaved">
        <v-icon start size="14">mdi-circle-medium</v-icon> ยังไม่บันทึก
      </v-chip>
      <v-chip
        v-if="!loading && !loadError"
        size="small"
        :color="valid ? 'success' : 'warning'"
        variant="tonal"
        class="mr-2"
        data-testid="tfb-compile-status"
      >
        <v-icon start size="14">{{ valid ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
        {{ valid ? 'สูตรใช้ได้' : `ผิดพลาด ${allErrors.length}` }}
      </v-chip>

      <v-btn icon size="small" variant="text" :disabled="!canUndo" data-testid="tfb-undo" @click="undo">
        <v-icon>mdi-undo</v-icon>
        <v-tooltip activator="parent" location="top">ย้อนกลับ</v-tooltip>
      </v-btn>
      <v-btn icon size="small" variant="text" :disabled="!canRedo" data-testid="tfb-redo" @click="redo">
        <v-icon>mdi-redo</v-icon>
        <v-tooltip activator="parent" location="top">ทำซ้ำ</v-tooltip>
      </v-btn>
      <v-btn icon size="small" variant="text" data-testid="tfb-layout" @click="doCleanup">
        <v-icon>mdi-vector-arrange-below</v-icon>
        <v-tooltip activator="parent" location="top">จัดวางอัตโนมัติ</v-tooltip>
      </v-btn>
      <v-btn icon size="small" variant="text" data-testid="tfb-fit" @click="doFit">
        <v-icon>mdi-fit-to-page-outline</v-icon>
        <v-tooltip activator="parent" location="top">พอดีจอ</v-tooltip>
      </v-btn>

      <v-btn
        color="white"
        variant="elevated"
        prepend-icon="mdi-content-save-outline"
        :disabled="!canSave"
        :loading="saving"
        class="tfb-text-primary ml-2"
        data-testid="tfb-save"
        @click="doSave"
      >บันทึกสูตร</v-btn>
    </v-toolbar>

    <div v-if="loadError" class="tfb-center">
      <v-icon size="42" color="error">mdi-alert-circle-outline</v-icon>
      <span class="mt-3 text-error">{{ loadError }}</span>
      <v-btn class="mt-4" variant="tonal" color="primary" @click="loadAll">ลองอีกครั้ง</v-btn>
    </div>
    <div v-else-if="loading" class="tfb-center">
      <v-progress-circular indeterminate color="primary" size="48" />
      <span class="mt-3 text-grey">กำลังโหลดสูตร…</span>
    </div>

    <template v-else>
      <!-- ===== Formula metadata ===== -->
      <div class="tfb-meta">
        <v-text-field
          v-model="meta.name"
          label="ชื่อสูตร *"
          hint="ชื่อที่คนอ่าน เช่น ค่าใช้จ่ายเหมา ม.40(5)"
          persistent-hint
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="tfb-meta-name"
          data-testid="tfb-name"
          @update:model-value="onMetaChange"
        />
        <v-text-field
          v-model="meta.outputVar"
          label="รหัสสูตร *"
          hint="สูตรอื่นและ function จะอ้างถึงสูตรนี้ด้วยรหัสนี้"
          persistent-hint
          density="compact"
          variant="outlined"
          hide-details="auto"
          class="tfb-meta-ov"
          data-testid="tfb-outputvar"
          @update:model-value="onMetaChange"
        />
        <v-text-field
          v-model="meta.notes"
          label="คำอธิบาย"
          density="compact"
          variant="outlined"
          hide-details
          class="tfb-meta-notes"
          @update:model-value="onMetaChange"
        />
      </div>

      <!-- ===== Blockly workspace ===== -->
      <div class="tfb-body">
        <div ref="blocklyDiv" class="tfb-workspace" data-testid="tfb-workspace"></div>
      </div>

      <!-- ===== Compiled bar ===== -->
      <div class="tfb-compiled-bar" aria-live="polite">
        <div class="tfb-compiled-main">
          <span class="tfb-compiled-label">{{ meta.outputVar || 'output_var' }} =</span>
          <code class="tfb-compiled-expr" data-testid="tfb-compiled-expr">{{ compiledExpr || '—' }}</code>
        </div>
        <div class="tfb-compiled-meta">
          <span :class="['tfb-charcount', { 'tfb-over': overLength }]">{{ charCount }}/{{ maxLen }}</span>
          <v-btn
            size="small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-text-box-edit-outline"
            data-testid="tfb-edit-text"
            @click="openPasteDialog"
          >แก้เป็นข้อความ</v-btn>
        </div>
      </div>

      <div v-if="allErrors.length" class="tfb-error-list">
        <div v-for="(msg, i) in allErrors" :key="`e-${i}`" class="tfb-error-item" data-testid="tfb-error">
          <v-icon size="14" color="error">mdi-alert-circle-outline</v-icon>
          <span>{{ msg }}</span>
        </div>
      </div>
    </template>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom" timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- ===== TEXT ⇄ BLOCKS · แก้สูตรเป็นข้อความ / วางสูตรจาก LLM =====
         The canvas is NOT touched while this dialog is open — everything here is a preview of what
         "สร้างบล็อกจากข้อความ" would do. See applyPastedFormula for why that matters. -->
    <v-dialog v-model="paste.show" max-width="780" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start size="20">mdi-text-box-edit-outline</v-icon> แก้สูตรเป็นข้อความ
        </v-card-title>
        <v-card-subtitle class="text-wrap">
          วางสูตรที่เขียนไว้ข้างนอก (เช่นให้ LLM ช่วยคิด) หรือแก้ข้อความด้านล่างได้เลย
          แล้วกด “สร้างบล็อกจากข้อความ” เพื่อสร้างบล็อกใหม่ตามข้อความนี้
        </v-card-subtitle>
        <v-card-text>
          <v-textarea
            v-model="paste.text"
            label="สูตร (NCalc)"
            rows="4"
            auto-grow
            density="compact"
            variant="outlined"
            hide-details
            class="tfb-paste-input"
            data-testid="tfb-paste-input"
            autofocus
          />

          <template v-if="pasteResult">
            <!-- what it parsed to -->
            <div v-if="pasteResult.ok" class="tfb-paste-preview mt-3" data-testid="tfb-paste-preview">
              <span class="tfb-compiled-label">{{ meta.outputVar || 'output_var' }} =</span>
              <code class="tfb-compiled-expr">{{ pasteResult.canonical || pasteResult.expression }}</code>
            </div>

            <!-- what is wrong (never a raw parse error) -->
            <v-alert
              v-if="pasteResult.problems.length"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tfb-paste-problems"
            >
              <div v-for="(p, i) in pasteResult.problems" :key="`pp-${i}`" class="tfb-paste-line">{{ p.message }}</div>
            </v-alert>

            <!-- what we changed for them — every repair, never silent -->
            <v-alert
              v-if="pasteResult.repairs.length"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tfb-paste-repairs"
            >
              <div class="font-weight-medium mb-1">ปรับให้อัตโนมัติ {{ pasteResult.repairs.length }} จุด</div>
              <div v-for="(r, i) in pasteResult.repairs" :key="`pr-${i}`" class="tfb-paste-line">• {{ r.message }}</div>
            </v-alert>

            <!-- names that will land wearing a "!" — an LLM invents variable names constantly -->
            <v-alert
              v-if="pasteUnknownIdents.length"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tfb-paste-unknown"
            >
              ตัวแปรที่ไม่มีในเวอร์ชันนี้:
              <code v-for="n in pasteUnknownIdents" :key="n" class="tfb-paste-ident">{{ n }}</code>
              — บล็อกจะขึ้นเครื่องหมาย “!” ให้ตรวจชื่ออีกครั้งก่อนบันทึก
            </v-alert>

            <!-- path ROOTs that match no rule code: a wrong one resolves to nothing and contributes 0 -->
            <v-alert
              v-if="pasteUnknownPathRoots.length"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tfb-paste-unknown-scope"
            >
              ไม่พบรหัส rule นี้ในเวอร์ชันนี้:
              <code v-for="s in pasteUnknownPathRoots" :key="s" class="tfb-paste-ident">{{ s }}</code>
              — เส้นทางข้อมูลจะหาข้อมูลไม่เจอและได้ผลลัพธ์เป็น 0 โดยไม่มีข้อความเตือน ให้ตรวจตัวพิมพ์เล็ก/ใหญ่และชื่อรหัสอีกครั้ง
            </v-alert>

            <!-- the name from a `name = …` line: surfaced, never applied behind their back -->
            <v-alert
              v-if="pastedOutputVarDiffers"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
              data-testid="tfb-paste-outputvar"
            >
              <div class="d-flex align-center flex-wrap ga-2">
                <span>
                  ข้อความระบุรหัสสูตร <code class="tfb-paste-ident">{{ pasteResult.assignedOutputVar }}</code>
                  ต่างจากรหัสปัจจุบัน <code class="tfb-paste-ident">{{ meta.outputVar || '—' }}</code>
                </span>
                <v-spacer />
                <v-btn size="small" variant="tonal" color="primary" data-testid="tfb-paste-adopt" @click="adoptPastedOutputVar">
                  ใช้รหัสนี้
                </v-btn>
              </div>
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <span class="text-caption text-medium-emphasis ml-2">บล็อกเดิมจะยังอยู่จนกว่าจะกดสร้าง · กด undo ย้อนกลับได้</span>
          <v-spacer />
          <v-btn variant="text" data-testid="tfb-paste-cancel" @click="paste.show = false">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            variant="tonal"
            :disabled="!pasteResult || !pasteResult.ok"
            data-testid="tfb-paste-apply"
            @click="applyPastedFormula"
          >สร้างบล็อกจากข้อความ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- rename blast radius -->
    <v-dialog v-model="rename.show" max-width="480" persistent>
      <v-card>
        <v-card-title>เปลี่ยนรหัสสูตร</v-card-title>
        <v-card-text>
          <div class="tfb-rename-codes">
            <code class="tfb-rename-old">{{ rename.oldCode }}</code>
            <v-icon size="18" class="mx-2">mdi-arrow-right</v-icon>
            <code class="tfb-rename-new">{{ rename.newCode }}</code>
          </div>
          <p class="mt-4 mb-2">มี {{ rename.deps.length }} สูตรที่เรียกรหัสนี้อยู่:</p>
          <ul class="tfb-rename-deps">
            <li v-for="d in rename.deps" :key="d.formulaID">
              {{ d.name || d.outputVar }} <code>{{ d.outputVar }}</code>
            </li>
          </ul>
          <p class="mt-3 mb-0 text-medium-emphasis">ทั้งหมดจะถูกแก้ให้ใช้รหัสใหม่พร้อมกัน</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="tfb-rename-cancel" @click="cancelRename">ยกเลิก</v-btn>
          <v-btn color="primary" variant="tonal" data-testid="tfb-rename-confirm" @click="confirmRename">
            บันทึกทั้งหมด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- unsaved-changes guard -->
    <v-dialog v-model="showLeaveDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>ยังไม่ได้บันทึก</v-card-title>
        <v-card-text>ออกจากหน้านี้แล้วการแก้ไขสูตรจะหายไป ต้องการออกหรือไม่?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelLeave">อยู่ต่อ</v-btn>
          <v-btn color="error" variant="tonal" @click="confirmLeave">ออกโดยไม่บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Blockly from '../utils/blockly/blocklyCore.js'
import { registerTaxBlocks, TAX_FORMULA_RESULT_TYPE } from '../utils/blockly/taxBlocks.js'
import { pickTaxTheme } from '../utils/blockly/taxTheme.js'
import { buildFormulaToolbox, registerToolboxCallbacks } from '../utils/blockly/taxToolbox.js'
import { applyBlockAnnotations, buildVariableCatalog } from '../utils/blockly/taxAnnotate.js'
import { workspaceToExpression, MAX_EXPRESSION_LEN } from '../utils/blockly/ncalcGenerator.js'
import { ncalcToBlocks, chooseFormulaSource, normalizeExpr } from '../utils/blockly/ncalcToBlocks.js'
import { normalizePastedFormula } from '../utils/blockly/pasteFormula.js'
import { classifyField } from '../utils/formulaGraph.js'
import { dependentsOf, rewriteRefs } from '../utils/blockly/renameFormulaRefs.js'
import { taxCalculateApi } from '../services/taxCalculateApi'
import { taxConfigApi } from '../services/taxConfigApi'
import { taxCalculateStore, referenceScan } from '../utils/taxCalculateStore'

const RECOMPUTE_DEBOUNCE_MS = 150
// The palette renders at this scale no matter how the canvas is zoomed (see pinFlyoutScale). Kept equal
// to zoom.startScale so the palette looks the same as it does on a freshly-opened formula.
const FLYOUT_SCALE = 0.9
const IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/g
// Where a fresh result block lands on an empty canvas, and how far LEFT of an adopted legacy tree it
// is placed so the tree stays roughly where its author left it (the label's own width — connecting
// pulls the child to the parent, not the other way round).
const RESULT_SPAWN_XY = { x: 24, y: 24 }
const RESULT_LABEL_W = 200
const RESULT_STACK_GAP = 120
const FUNCTIONS = new Set(['MIN', 'MAX', 'ROUND', 'IF'])

export default {
  name: 'TaxFormulaBlockly',
  data() {
    return {
      loading: true,
      loadError: '',
      saving: false,
      configVersionId: this.$route.params.configVersionId,
      calcVersionId: Number(this.$route.params.calcVersionId),
      formulaId: (() => {
        const raw = this.$route.params.formulaId
        if (raw === 'new') return null
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
      })(),
      full: null,
      // null (not []) is the "never heard back" state for BOTH — see catalogComplete. Only loadAll
      // may promote them to a real (possibly empty) list.
      rules: null,
      variablesLoaded: false,
      // `paths` is the tax form's nested-path vocabulary (S1-P10) — the ONLY authoring path for a
      // tax_path block. An absent group is normal (the backend may not ship it yet) and renders the
      // เส้นทางข้อมูล category's "ยังไม่มี…" label; it never becomes a typeable box.
      catalog: { personalInfo: [], globals: [], paths: [] },
      maxLen: MAX_EXPRESSION_LEN,
      // evalOrder is carried but never edited here — it is a pure list-display order (the engine
      // derives the real evaluation order from which formulas an expression references).
      meta: {
        name: '',
        outputVar: '',
        evalOrder: 0,
        notes: '',
        isActive: true,
      },
      // The PERSISTED รหัสสูตร, kept apart from meta.outputVar so a rename can be detected on save and
      // the formulas that reference the old code can be carried along with it.
      savedOutputVar: '',
      rename: { show: false, oldCode: '', newCode: '', deps: [] },
      // TEXT ⇄ BLOCKS. `text` is the ONLY thing typing touches — the canvas is rebuilt exclusively by
      // applyPastedFormula, so a half-typed formula can never destroy the author's blocks.
      paste: { show: false, text: '' },
      compiledExpr: '',
      compileErrors: [],
      // "The canvas is not what you pasted" — the single most serious thing this feature can say, so
      // it lives in the persistent error list with every other hard problem. It used to be a 3.2s
      // auto-dismissing snackbar: an author who looked away missed the one warning that says their
      // tax formula is not the formula they read. Cleared when they next touch the text dialog.
      pasteMismatch: '',
      // "This stored formula could not be opened" — the loudest thing this editor can say about a
      // formula that already exists in the database, so it lives in the persistent error list rather
      // than in a snackbar that auto-dismisses in three seconds. It is how a RETIRED construct
      // (SUMITEMS, item_amount/…) reaches the person who has to migrate it: they opened the one
      // formula that still uses it, and the message names the path to write instead. Cleared only by
      // opening something else.
      loadProblem: '',
      valid: false,
      dirty: false,
      canUndo: false,
      canRedo: false,
      topPad: 48,
      pageHeight: null,
      snackbar: { show: false, color: 'success', message: '' },
      showLeaveDialog: false,
      pendingRoute: null,
    }
  },
  computed: {
    pageStyle() {
      return {
        paddingTop: this.topPad + 'px',
        height: this.pageHeight != null ? this.pageHeight + 'px' : 'calc(100vh - 64px)',
      }
    },
    isNewFormula() {
      return this.formulaId == null
    },
    versionCode() {
      return (this.full && this.full.version && this.full.version.versionCode) || ''
    },
    /** Every OTHER formula of this calc version — the toolbox reference list + dup/cycle checks. */
    siblings() {
      const list = (this.full && this.full.formulas) || []
      return list.filter((f) => f && (this.formulaId == null || f.formulaID !== this.formulaId))
    },
    charCount() {
      return (this.compiledExpr || '').length
    },
    overLength() {
      return this.charCount > this.maxLen
    },
    metaErrors() {
      const errs = []
      if (!String(this.meta.name || '').trim()) errs.push('ต้องตั้งชื่อสูตร')
      const ov = String(this.meta.outputVar || '').trim().toLowerCase()
      if (!ov) errs.push('ต้องตั้งรหัสสูตร')
      else if (!/^[a-z_][a-z0-9_]*$/.test(ov)) errs.push('รหัสสูตรต้องเป็น lower_snake_case (a-z, 0-9, _)')
      if (ov && this.siblings.some((f) => String(f.outputVar || '').trim().toLowerCase() === ov)) {
        errs.push(`รหัสสูตรซ้ำกับสูตรอื่น: ${ov}`)
      }
      // A cycle is a hard engine error (the engine topologically sorts by reference and throws),
      // so it must be caught here rather than merely warned about.
      const cyc = this.cyclePath
      if (cyc) errs.push(`สูตรอ้างอิงวนกัน (cycle): ${cyc.join(' → ')}`)
      return errs
    },
    /** Cycle among formula references, including this formula's in-progress expression. */
    cyclePath() {
      const self = String(this.meta.outputVar || '').trim().toLowerCase()
      if (!self || !this.compiledExpr) return null

      const ovSet = new Set([self])
      this.siblings.forEach((f) => {
        const v = String(f.outputVar || '').trim().toLowerCase()
        if (v) ovSet.add(v)
      })
      const depsOf = (expr) => {
        const out = new Set()
        // Through the MASK: a text literal's content is data, and a PATH is ONE name whose segments
        // are field names in the form JSON. Unmasked, a version holding a formula named `amount`
        // (a perfectly legal lower_snake รหัสสูตร) gains a dependency on itself from every §40(5)
        // path — a PHANTOM cycle that blocks the save of a formula with nothing wrong with it.
        // Same two-step, same order, as the backend's TaxCalcEngineBAL.ReferenceScan.
        const s = referenceScan(String(expr || ''))
        let m
        IDENT_RE.lastIndex = 0
        while ((m = IDENT_RE.exec(s)) !== null) {
          const low = m[0].toLowerCase()
          if (!FUNCTIONS.has(m[0].toUpperCase()) && ovSet.has(low)) out.add(low)
        }
        return out
      }
      const graph = new Map()
      this.siblings.forEach((f) => {
        const v = String(f.outputVar || '').trim().toLowerCase()
        if (v) graph.set(v, depsOf(f.expression))
      })
      graph.set(self, depsOf(this.compiledExpr))

      const visiting = new Set()
      const done = new Set()
      const path = []
      const dfs = (n) => {
        if (visiting.has(n)) return path.slice(path.indexOf(n)).concat(n)
        if (done.has(n)) return null
        visiting.add(n)
        path.push(n)
        for (const d of graph.get(n) || []) {
          const hit = dfs(d)
          if (hit) return hit
        }
        visiting.delete(n)
        done.add(n)
        path.pop()
        return null
      }
      return dfs(self)
    },
    allErrors() {
      return [
        ...(this.loadProblem ? [this.loadProblem] : []),
        ...this.compileErrors,
        ...this.metaErrors,
        ...(this.pasteMismatch ? [this.pasteMismatch] : []),
      ]
    },
    /**
     * Live normalize of whatever is in the text box — repairs, problems and the parsed result, on
     * EVERY keystroke. Pure (no workspace, no I/O), which is exactly why it can run this eagerly: the
     * author sees what applying WOULD do before anything happens to their canvas.
     */
    pasteResult() {
      if (!this.paste.show) return null
      return normalizePastedFormula(this.paste.text, { outputVar: this.meta.outputVar })
    },
    /**
     * The identifiers in the pasted text that resolve in NO catalog group — they will land on the
     * canvas wearing a "!". An LLM invents variable names constantly (it cannot know this version's
     * รหัสสูตร or rule codes), so this is the COMMON outcome of a paste, not an edge case, and it is
     * far cheaper to read here than to hunt for on the canvas afterwards.
     *
     * Gated on catalogComplete for the same reason the block "!" is (see that computed): with a failed
     * rules/variables fetch, "not in the catalog" means "we do not know", and listing every name in the
     * formula as unknown would be an alarm on everything, i.e. an alarm on nothing.
     */
    pasteUnknownIdents() {
      const r = this.pasteResult
      if (!r || !r.ok || !this.catalogComplete) return []
      const catalog = this.classifyCatalog()
      return r.identifiers.filter((n) => classifyField(n, catalog).flagged)
    },
    /**
     * The PATH ROOTs in the pasted text that match no rule code in this version.
     *
     * A root is a rule CODE, not free text: the engine resolves the path against that rule's form
     * data, so a wrong one finds nothing and contributes 0 — a silent zero, with no error at save and
     * no "!" on the canvas either (classifyField can only flag a root it was given a catalog for, and
     * this dialog runs before any block exists). The rule list is right here; comparing
     * case-insensitively, exactly like every other name comparison in this path, is the whole check.
     */
    pasteUnknownPathRoots() {
      const r = this.pasteResult
      if (!r || !r.ok || !r.pathRoots || !r.pathRoots.length || !this.catalogComplete) return []
      const codes = new Set((Array.isArray(this.rules) ? this.rules : [])
        .map((x) => String((x && (x.ruleCode || x.RuleCode)) || '').trim().toLowerCase())
        .filter(Boolean))
      return r.pathRoots.filter((s) => !codes.has(String(s).trim().toLowerCase()))
    },
    /** A `name = …` line whose name is NOT the current รหัสสูตร — offered, never applied on its own. */
    pastedOutputVarDiffers() {
      const r = this.pasteResult
      if (!r || !r.assignedOutputVar) return false
      return String(r.assignedOutputVar).toLowerCase() !== String(this.meta.outputVar || '').trim().toLowerCase()
    },
    canSave() {
      return !this.saving && this.valid && this.metaErrors.length === 0
    },
    /**
     * May a "not in the catalog" be reported to the author as "deleted from the version" (the ! + ⚠)?
     *
     * ONLY when every group classifyField consults is real. `full` is required — loadAll throws
     * without it — so `constants` / `outputVars` are always the version's live lists and an empty one
     * genuinely means "none exist". `rules` and `variables` are best-effort (`.catch(() => null)`),
     * and null there means the fetch FAILED, not that the admin deleted everything: classifying
     * against those empties would flag every rule/personal/global block on the canvas at once — an
     * alarm on every block is an alarm on none.
     */
    catalogComplete() {
      return this.rules !== null && this.variablesLoaded
    },
    isDark() {
      const t = this.$vuetify && this.$vuetify.theme
      if (!t) return false
      const cur = t.current
      if (cur && typeof cur.dark === 'boolean') return cur.dark
      if (cur && cur.value && typeof cur.value.dark === 'boolean') return cur.value.dark
      const name = t.global && t.global.name
      return String(name && name.value != null ? name.value : name) === 'dark'
    },
  },
  watch: {
    isDark(val) {
      if (this._ws) {
        try { this._ws.setTheme(pickTaxTheme(val)) } catch (_e) { /* ignore */ }
      }
    },
  },
  async mounted() {
    registerTaxBlocks()
    this._beforeUnload = (e) => {
      if (this.dirty) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', this._beforeUnload)
    this._onResize = () => this.updateLayoutInsets()
    window.addEventListener('resize', this._onResize)
    this.$nextTick(() => this.updateLayoutInsets())
    await this.loadAll()
    this.$nextTick(() => this.updateLayoutInsets())
  },
  beforeUnmount() {
    if (this._beforeUnload) window.removeEventListener('beforeunload', this._beforeUnload)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    if (this._resizeObserver) {
      try { this._resizeObserver.disconnect() } catch (_e) { /* ignore */ }
      this._resizeObserver = null
    }
    if (this._ws) {
      try { this._ws.removeChangeListener(this.onWorkspaceChange) } catch (_e) { /* ignore */ }
      try { this._ws.dispose() } catch (_e) { /* ignore */ }
      this._ws = null
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      this.pendingRoute = next
      this.showLeaveDialog = true
    } else {
      next()
    }
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.loadError = ''
      try {
        const [variables, full, rules] = await Promise.all([
          taxCalculateApi.getVariables().catch(() => null),
          taxCalculateApi.getFull(this.calcVersionId),
          taxConfigApi.getRules(this.configVersionId).catch(() => null),
        ])
        if (!full || !full.version) throw new Error('ไม่พบ calc version นี้')
        this.full = full
        this.rules = Array.isArray(rules) ? rules : null
        this.variablesLoaded = !!variables
        if (variables) taxCalculateStore.setVariableCatalog(variables)
        this.buildCatalog(variables)

        if (this.isNewFormula) {
          const maxOrder = ((full.formulas || []).reduce((m, f) => Math.max(m, Number(f.evalOrder) || 0), -1)) + 1
          this.meta = { name: '', outputVar: '', evalOrder: maxOrder, notes: '', isActive: true }
          this.savedOutputVar = ''
        } else {
          const f = (full.formulas || []).find((x) => x && x.formulaID === this.formulaId)
          if (!f) throw new Error('ไม่พบสูตรนี้ในเวอร์ชัน')
          this.meta = {
            name: f.name || '',
            outputVar: f.outputVar || '',
            evalOrder: Number(f.evalOrder) || 0,
            notes: f.notes || '',
            isActive: f.isActive !== false,
          }
          this.savedOutputVar = f.outputVar || ''
        }
      } catch (err) {
        this.loadError = (err && err.message) ? err.message : 'โหลดข้อมูลไม่สำเร็จ'
      } finally {
        this.loading = false
      }
      if (!this.loadError) {
        await this.$nextTick()
        this.setupWorkspace()
      }
    },
    /**
     * Keep each variable's catalog Description (TaxVariableDto) — it is the only gloss these blocks
     * have. Shaping lives in taxAnnotate.buildVariableCatalog: it is the one place that knows the
     * ItemScope group exists (and that an older backend still ships those names inside Globals), and
     * a null `variables` (failed fetch) yields three empty groups, exactly as before.
     */
    buildCatalog(variables) {
      this.catalog = buildVariableCatalog(variables)
    },
    /** Per-rule bridge vars the engine puts in the global bag: the amount + its เหมา rate. */
    ruleBridgeVars() {
      const out = []
      ;(Array.isArray(this.rules) ? this.rules : []).forEach((r) => {
        const code = String((r && (r.ruleCode || r.RuleCode)) || '').trim().toLowerCase()
        if (code) out.push(code, `${code}_rate`)
      })
      return out
    },

    // ── Blockly ──
    setupWorkspace() {
      const host = this.$refs.blocklyDiv
      if (!host) return
      this._ws = Blockly.inject(host, {
        toolbox: buildFormulaToolbox(this.toolboxCtx()),
        theme: pickTaxTheme(this.isDark),
        renderer: 'zelos',
        trashcan: true,
        // Trackpad: two-finger scroll pans, pinch zooms. Blockly's wheel handler routes to zoom when
        // `ctrlKey || metaKey || !move.wheel` — and the browser synthesizes ctrlKey for a pinch — so BOTH
        // wheel flags must be on. With move.wheel off, `!move.wheel` holds and every scroll zooms instead.
        zoom: { controls: true, wheel: true, startScale: 0.9, maxScale: 2, minScale: 0.3, scaleSpeed: 1.2 },
        grid: { spacing: 24, length: 3, snap: true },
        move: { scrollbars: true, drag: true, wheel: true },
      })
      registerToolboxCallbacks(this._ws, this.toolboxCtx())
      this.pinFlyoutScale()
      this._ws.addChangeListener(this.onWorkspaceChange)
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          if (this._ws) { try { Blockly.svgResize(this._ws) } catch (_e) { /* ignore */ } }
        })
        this._resizeObserver.observe(host)
      }
      this.loadWorkspaceFromFormula()
      // After BOTH load branches and for a brand-new formula — one anchor, whatever we opened.
      this.ensureResultRoot()
      this.dirty = false
      this.$nextTick(() => {
        if (this._ws) {
          try { Blockly.svgResize(this._ws) } catch (_e) { /* ignore */ }
          this.doFit()
        }
      })
      this.recompute()
    },
    /**
     * Hold the palette at a constant size while the canvas zooms.
     *
     * Blockly's `Flyout.getFlyoutScale()` returns the target workspace's LIVE zoom, and `setScale` calls
     * `flyout.reflow()` → `reflowInternal_` assigns that straight to the flyout's own scale — so every
     * zoom (and every `zoomToFit`) resizes the palette with the canvas. The palette is chrome, not
     * content: it has to stay legible whatever the canvas is doing.
     *
     * The guard is per-flyout, not per-view: `updateToolbox` may hand back a new flyout instance, and the
     * fresh one must be pinned too.
     */
    pinFlyoutScale() {
      const flyout = this._ws && typeof this._ws.getFlyout === 'function' ? this._ws.getFlyout(false) : null
      if (!flyout || flyout._taxScalePinned) return
      flyout.getFlyoutScale = () => FLYOUT_SCALE
      flyout._taxScalePinned = true
      try { flyout.reflow() } catch (_e) { /* not rendered yet — the next reflow applies it */ }
    },
    toolboxCtx() {
      return {
        personalInfo: this.catalog.personalInfo,
        globals: this.catalog.globals,
        // The tax form's nested paths — the ONLY authoring path for a tax_path block (its PATH label is
        // fixed text, exactly like a variable NAME and a Σ SCOPE). Empty is a normal first state and the
        // category says so; it must never become a typeable box.
        paths: this.catalog.paths,
        getRules: () => this.rules || [],
        getConstants: () => (this.full && this.full.constants) || [],
        // "ดึงสูตรอื่นมาเป็นตัวแปร" — every sibling formula of this calc version.
        getOutputVars: () => this.siblings.map((f) => ({ outputVar: f.outputVar, label: f.name || '' })),
        getSelfOutputVar: () => this.meta.outputVar,
      }
    },
    classifyCatalog() {
      return {
        personalInfo: this.catalog.personalInfo,
        globals: this.catalog.globals,
        constants: ((this.full && this.full.constants) || []).map((c) => String(c.constKey || '').toUpperCase()),
        outputVars: ((this.full && this.full.formulas) || []).map((f) => String(f.outputVar || '')).filter(Boolean),
        ruleInputs: this.ruleBridgeVars(),
      }
    },
    /**
     * Re-derive the badge + description + stale marker + tooltip of every annotated block on the canvas
     * (tax_variable AND tax_path) from the LIVE catalog. All are DISPLAY-ONLY (none is ever
     * compiled), so re-deriving them cannot drift the expression — and all MUST be re-derived, or a
     * block contradicts itself: retype a rate block's NAME to `age` and the gloss follows while a frozen
     * badge still reads Rate/cyan; load a stored Σ and its cached `flagged` bit outlives the rule.
     *
     * This is also where the "!" comes from: a rule dropped from the config version, or a ค่าคงที่ /
     * สูตร deleted from the calc version, only stops resolving HERE — the stored graph still holds the
     * name it was authored with, and nothing rewrites it.
     *
     * The pass itself lives in utils/blockly/taxAnnotate.js (headless, so it is under test); this method
     * is the view's binding of it: which workspace, which catalog, and whether a "!" may be shown at all.
     */
    applyVariableDescriptions() {
      applyBlockAnnotations(this._ws, {
        // A getter, not a snapshot: the tooltip closures resolve lazily, so a hovered block reads
        // current even between re-derivation passes.
        ctx: () => this.toolboxCtx(),
        catalog: this.classifyCatalog(),
        // A stale name is only knowable against a catalog we actually have. When a fetch failed, leave
        // every block unflagged rather than accusing all of them (see catalogComplete). The badge still
        // re-derives: a wrong badge is cosmetic, a false "!" tells the author to fix a live formula.
        canFlag: this.catalogComplete,
      })
    },
    /** Load the stored per-formula graph (keeping layout), else rebuild the tree from Expression. */
    loadWorkspaceFromFormula() {
      if (!this._ws || this.isNewFormula) return
      const f = ((this.full && this.full.formulas) || []).find((x) => x && x.formulaID === this.formulaId)
      if (!f) return
      const disable = Blockly.Events && typeof Blockly.Events.disable === 'function'
      if (disable) Blockly.Events.disable()
      try {
        const decision = chooseFormulaSource(f.expressionGraphJson, f.expression)
        if (decision.source === 'workspace') {
          let blob = f.expressionGraphJson
          if (typeof blob === 'string') { try { blob = JSON.parse(blob) } catch (_e) { blob = null } }
          if (blob && blob.workspace) {
            Blockly.serialization.workspaces.load(blob.workspace, this._ws)
            return
          }
        }
        // Rebuild from the persisted expression (source of truth). ncalcToBlocks is model-only
        // (newBlock, no initSvg), so re-hydrate through serialization to get rendered blocks.
        try {
          ncalcToBlocks(this._ws, f.expression, { catalog: this.classifyCatalog() })
          if (this._ws.rendered) {
            const rebuilt = Blockly.serialization.workspaces.save(this._ws)
            this._ws.clear()
            Blockly.serialization.workspaces.load(rebuilt, this._ws)
            if (typeof this._ws.cleanUp === 'function') {
              try { this._ws.cleanUp() } catch (_e) { /* ignore */ }
            }
          }
        } catch (err) {
          // PERSISTENT, not a snackbar. The stored Expression is still in the database and is still
          // what the engine runs; the canvas being empty does not change that, and "เริ่มใหม่ได้เลย"
          // in a toast that disappears is an invitation to overwrite a formula nobody read. Save is
          // already blocked (an empty anchor is a compile error), so what is left to do is SAY why,
          // and keep saying it — a retired construct's migration sentence is the whole point.
          this.loadProblem = 'เปิดสูตรเดิมไม่ได้ — ' + ((err && err.message) || 'parse error')
            + ' · สูตรที่บันทึกไว้คือ: ' + String(f.expression || '')
        }
      } finally {
        if (disable) Blockly.Events.enable()
      }
    },
    /**
     * Guarantee exactly ONE tax_formula_result on the canvas — the compile anchor. Only its VALUE
     * subtree is compiled and saved; everything else the author leaves lying around is scratch.
     *
     * A formula saved before this block existed is a BARE tree, so a legacy one is adopted into the
     * anchor: the formula opens clean and keeps its blocks instead of greeting its author with an error
     * about a floating block they never made.
     *
     * setDeletable(false) IS the "exactly one" guarantee — it cannot be dragged to the trash, and
     * buildFormulaToolbox never offers a second. Events stay disabled: opening a formula is not editing
     * it, so the upgrade must not raise ยังไม่บันทึก.
     *
     * Every anchor BEYOND the keeper is forced back to deletable. The save gate rejects a canvas with
     * two anchors, so a non-deletable spare would be an error the author has no way to clear — the
     * formula would be unopenable-and-unsavable forever. The keeper is the anchor that already holds a
     * formula, so the spare is the empty one.
     */
    ensureResultRoot() {
      const ws = this._ws
      if (!ws) return
      const disable = Blockly.Events && typeof Blockly.Events.disable === 'function'
      if (disable) Blockly.Events.disable()
      try {
        const tops = (ws.getTopBlocks(false) || []).filter(
          (b) => b && !(typeof b.isInsertionMarker === 'function' && b.isInsertionMarker()),
        )
        const anchors = tops.filter((b) => b.type === TAX_FORMULA_RESULT_TYPE)
        let result = anchors.find((b) => b.getInputTargetBlock('VALUE')) || anchors[0]
        if (!result) {
          const roots = tops.filter((b) => b.outputConnection)
          const adopt = roots.length === 1 ? roots[0] : null
          result = ws.newBlock(TAX_FORMULA_RESULT_TYPE)
          if (typeof result.initSvg === 'function') {
            try { result.initSvg() } catch (_e) { /* headless — ignore */ }
          }
          const at = adopt ? this.adoptXY(adopt) : this.spawnXY(tops)
          try { result.moveBy(at.x, at.y) } catch (_e) { /* not rendered — ignore */ }
          if (adopt) {
            try { result.getInput('VALUE').connection.connect(adopt.outputConnection) } catch (_e) { /* ignore */ }
          }
          if (typeof result.render === 'function') {
            try { result.render() } catch (_e) { /* headless — ignore */ }
          }
        }
        if (typeof result.setDeletable === 'function') result.setDeletable(false)
        anchors.forEach((b) => {
          if (b !== result && typeof b.setDeletable === 'function') b.setDeletable(true)
        })
      } finally {
        if (disable) Blockly.Events.enable()
      }
    },
    /** Place the anchor just left of a legacy tree, so adopting it barely moves it. */
    adoptXY(block) {
      const xy = this.blockXY(block)
      return { x: Math.max(0, xy.x - RESULT_LABEL_W), y: xy.y }
    },
    /** An empty canvas gets the anchor up top; anything else gets it clear of what is already there. */
    spawnXY(blocks) {
      if (!blocks.length) return { ...RESULT_SPAWN_XY }
      const bottom = blocks.reduce((m, b) => Math.max(m, this.blockXY(b).y), 0)
      return { x: RESULT_SPAWN_XY.x, y: bottom + RESULT_STACK_GAP }
    },
    blockXY(block) {
      const xy = typeof block.getRelativeToSurfaceXY === 'function' ? block.getRelativeToSurfaceXY() : null
      return { x: (xy && xy.x) || 0, y: (xy && xy.y) || 0 }
    },
    onWorkspaceChange(event) {
      if (!this._ws) return
      if (event && event.isUiEvent) {
        this.syncUndoRedo()
        return
      }
      this.dirty = true
      this.scheduleRecompute()
    },
    scheduleRecompute() {
      if (this._debounceTimer) clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => {
        this._debounceTimer = null
        this.recompute()
      }, RECOMPUTE_DEBOUNCE_MS)
    },
    recompute() {
      if (!this._ws) return
      const { expression, errors } = workspaceToExpression(this._ws)
      this.compiledExpr = expression
      this.compileErrors = errors
      this.valid = errors.length === 0 && expression !== ''
      // Rides the same debounced pass as the compile: covers the initial load, a block dropped from a
      // flyout (its seeded desc does not survive the drag — extraState.desc is inbound-only) and a
      // NAME edited by hand.
      this.applyVariableDescriptions()
      this.syncUndoRedo()
    },
    syncUndoRedo() {
      if (!this._ws) return
      try {
        this.canUndo = this._ws.getUndoStack().length > 0
        this.canRedo = this._ws.getRedoStack().length > 0
      } catch (_e) { /* older Blockly — ignore */ }
    },
    onMetaChange() {
      this.dirty = true
      // The reference flyout excludes self by outputVar — refresh it after a rename.
      if (this._ws) {
        try { this._ws.updateToolbox(buildFormulaToolbox(this.toolboxCtx())) } catch (_e) { /* ignore */ }
        this.pinFlyoutScale()
      }
    },
    // ── TEXT ⇄ BLOCKS ──
    /**
     * Open the text editor PREFILLED with the current compiled expression, so this is "edit the
     * formula as text" as much as "paste a new one" — the author can tweak one coefficient without
     * hunting for the block, and an LLM-written formula can be dropped straight over it.
     */
    openPasteDialog() {
      // The compile is debounced; a dialog opened inside that window would prefill a stale string and
      // silently discard the last block the author moved.
      if (this._debounceTimer) { clearTimeout(this._debounceTimer); this._debounceTimer = null }
      this.recompute()
      this.pasteMismatch = '' // they are about to redo the paste — the old warning is about the old one
      this.paste = { show: true, text: this.compiledExpr || '' }
    },
    /**
     * Adopt the `name` from a `name = …` paste as รหัสสูตร — only ever on this explicit click.
     *
     * Lower-cased, because รหัสสูตร IS lower_snake_case (see metaErrors) and every other comparison in
     * this path is case-insensitive. Taking an LLM's `EXPENSE_40_5A` verbatim landed an invalid code
     * in the field and raised a validation error somewhere else on the page, for a click whose whole
     * promise was "use this name".
     */
    adoptPastedOutputVar() {
      const r = this.pasteResult
      if (!r || !r.assignedOutputVar) return
      this.meta.outputVar = String(r.assignedOutputVar).trim().toLowerCase()
      this.onMetaChange()
    },
    /**
     * Rebuild the canvas from the pasted text. The ONLY method here that touches the workspace.
     *
     * Order is the whole safety story:
     *   1. `pasteResult.ok` is false until the text PARSES, and the apply button is disabled until then
     *      — so nothing below can run on input that would have thrown half-way through a rebuild and
     *      left the author with a canvas that is neither their old formula nor their new one.
     *   2. The tree is built into a THROWAWAY workspace first. `ncalcToBlocks` is model-only (newBlock,
     *      no initSvg), and a throw there must cost nothing — the live canvas is still untouched at
     *      that point. The compile anchor is built into the same throwaway blob so that the whole
     *      thing (anchor + tree) arrives on the canvas as ONE serialization append.
     *   3. Everything that mutates the live workspace happens inside a single `Blockly.Events` group
     *      with recordUndo on — so ONE ctrl-Z / undo-button press puts the previous formula back. A
     *      rebuild the author cannot walk out of would make pasting a one-way door.
     *   4. Afterwards we RECOMPILE and compare against what was applied. Identical is the normal case;
     *      different means the canvas is not what they pasted, and they are told immediately rather
     *      than discovering it when the engine runs a formula they never wrote.
     */
    applyPastedFormula() {
      const r = this.pasteResult
      const ws = this._ws
      if (!r || !r.ok || !ws) return

      // 2) build off-canvas
      let blob = null
      try {
        const tmp = new Blockly.Workspace()
        try {
          const root = ncalcToBlocks(tmp, r.expression, { catalog: this.classifyCatalog() })
          const anchor = tmp.newBlock(TAX_FORMULA_RESULT_TYPE)
          if (root) anchor.getInput('VALUE').connection.connect(root.outputConnection)
          blob = Blockly.serialization.blocks.save(anchor)
        } finally {
          try { tmp.dispose() } catch (_e) { /* headless — ignore */ }
        }
      } catch (err) {
        // Belt and braces: pasteResult.ok already proved this parses (canonicalizeExpression built the
        // very same tree). If it still fails, the canvas has NOT been touched — say so and stop.
        this.showSnack('สร้างบล็อกจากข้อความไม่สำเร็จ — ' + ((err && err.message) || 'error') + ' (บล็อกเดิมยังอยู่)', 'error')
        return
      }
      if (!blob) return

      // 3) one undoable group
      const setGroup = Blockly.Events && typeof Blockly.Events.setGroup === 'function'
      if (setGroup) Blockly.Events.setGroup(true)
      try {
        ;(ws.getTopBlocks(false) || []).forEach((b) => {
          if (!b) return
          // `dispose()` is not gated on deletability (that guard is about the trashcan and the
          // toolbox), so we must NOT flip the anchor to deletable first: the undo record captures the
          // block AS IT WAS AT DISPOSE, and undoing a paste therefore restored an anchor the author
          // could then drag to the trash — with no way in the toolbox to make another, leaving the
          // formula unopenable-and-unsavable. ensureResultRoot() below (and after every undo/redo)
          // re-pins it either way; not touching it here is what keeps the undo record honest.
          try { b.dispose(false) } catch (_e) { /* already gone — ignore */ }
        })
        Blockly.serialization.blocks.append(blob, ws, { recordUndo: true })
        // The canvas is now the pasted formula, so a "could not open the stored one" banner is about
        // a formula that is no longer on screen. Cleared HERE (on apply) and not when the dialog
        // merely opened — cancelling must leave the warning standing.
        this.loadProblem = ''
        // INSIDE the group, both of them. `ensureResultRoot` disables events itself, but `doCleanup`
        // is `workspace.cleanUp()` — it MOVES every top block, and a move outside the group lands in
        // an undo entry of its own. One ctrl-Z would then only undo the tidy-up and leave the canvas
        // holding the pasted formula: a rebuild the author cannot walk out of in one step, which is
        // the whole promise of doing the dispose+append as a group.
        this.ensureResultRoot() // re-pin the anchor as the single non-deletable compile root
        this.doCleanup()
      } finally {
        if (setGroup) Blockly.Events.setGroup(false)
      }

      // Fit AFTER the dialog has closed and the canvas has its real size back — zoomToFit against a
      // half-laid-out workspace lands the fresh tree off-screen, which reads as "nothing happened".
      this.$nextTick(() => {
        if (this._ws) { try { Blockly.svgResize(this._ws) } catch (_e) { /* ignore */ } }
        this.doFit()
      })
      this.paste = { show: false, text: '' }
      this.dirty = true
      this.recompute()

      // 4) is the canvas what they pasted?
      // A mismatch means the formula that will be SAVED is not the formula the author read in the
      // preview — the exact "silently altered number" this whole path exists to prevent. It goes to
      // the persistent error list (not a snackbar that disappears in 3.2s) and stays there until they
      // fix it or re-open the text dialog.
      const applied = normalizeExpr(r.canonical || r.expression)
      const got = normalizeExpr(this.compiledExpr)
      if (applied !== got) {
        this.pasteMismatch = 'บล็อกที่สร้างได้ไม่ตรงกับข้อความที่วาง — ข้อความที่วางคือ "' + applied
          + '" แต่บล็อกบนกระดานอ่านค่าออกมาเป็น "' + (this.compiledExpr || '(ว่าง)')
          + '" ให้กด undo เพื่อย้อนกลับ แล้วตรวจสูตรอีกครั้งก่อนบันทึก'
        return
      }
      const n = r.repairs.length
      this.showSnack(n ? `สร้างบล็อกจากข้อความแล้ว (ปรับให้อัตโนมัติ ${n} จุด)` : 'สร้างบล็อกจากข้อความแล้ว', 'success')
    },

    // ensureResultRoot after BOTH: an undo/redo replays a serialization the author cannot inspect, and
    // the one invariant the editor cannot survive losing is "exactly one compile anchor, non-deletable"
    // — the toolbox offers no way to make another one.
    undo() { if (this._ws) { this._ws.undo(false); this.ensureResultRoot(); this.recompute() } },
    redo() { if (this._ws) { this._ws.undo(true); this.ensureResultRoot(); this.recompute() } },
    doCleanup() { if (this._ws && this._ws.cleanUp) { try { this._ws.cleanUp() } catch (_e) { /* ignore */ } } },
    doFit() { if (this._ws && this._ws.zoomToFit) { try { this._ws.zoomToFit() } catch (_e) { /* ignore */ } } },

    // ── Save ──
    async doSave() {
      // The compile is debounced, so re-run it first: a save fired inside that window would otherwise
      // persist whatever the LAST compile saw — a half-formula that 400s at calculation time for
      // whoever runs it. canSave and compiledExpr both read from this pass.
      if (this._debounceTimer) { clearTimeout(this._debounceTimer); this._debounceTimer = null }
      this.recompute()
      if (!this.canSave) {
        this.showSnack(this.allErrors[0] || 'สูตรยังไม่ถูกต้อง — แก้ให้ครบก่อนบันทึก', 'error')
        return
      }

      // A rename silently invalidates every formula pointing at the old code, so the author confirms
      // the blast radius before it happens — but only when there IS one.
      const oldCode = String(this.savedOutputVar || '').trim()
      const newCode = String(this.meta.outputVar || '').trim()
      if (oldCode && newCode && oldCode !== newCode) {
        const deps = dependentsOf((this.full && this.full.formulas) || [], oldCode, this.formulaId)
        if (deps.length) {
          this.rename = { show: true, oldCode, newCode, deps }
          return
        }
      }
      await this.persist([])
    },
    async confirmRename() {
      const { oldCode, newCode, deps } = this.rename
      this.rename = { ...this.rename, show: false }
      await this.persist(deps, oldCode, newCode)
    },
    cancelRename() {
      this.rename = { show: false, oldCode: '', newCode: '', deps: [] }
    },
    /**
     * Save this formula, then repoint its dependents.
     *
     * Order matters and cannot be reversed: save-time validation only allows OutputVars that already
     * exist, so a dependent cannot be moved to the new code until the rename itself has landed. Each
     * dependent is its own request — a failure part-way leaves the rest pointing at the old code, which
     * is why the survivors are named rather than swallowed. They stay visible as "!" blocks.
     */
    async persist(deps, oldCode, newCode) {
      this.saving = true
      try {
        const graph = JSON.stringify({
          graphVersion: 2,
          workspace: Blockly.serialization.workspaces.save(this._ws),
        })
        const req = taxCalculateStore.buildFormulaRequest({
          name: this.meta.name,
          expression: this.compiledExpr,
          expressionGraphJson: graph,
          outputVar: this.meta.outputVar,
          evalOrder: this.meta.evalOrder,
          notes: this.meta.notes,
          isActive: this.meta.isActive,
        }, this.meta.evalOrder)

        if (this.isNewFormula) {
          const created = await taxCalculateApi.createFormula(this.calcVersionId, req)
          const newId = created && created.formulaID
          if (newId == null) throw new Error('สร้างสูตรไม่สำเร็จ (missing id)')
          this.formulaId = Number(newId)
        } else {
          await taxCalculateApi.updateFormula(this.formulaId, req)
        }
        this.savedOutputVar = String(this.meta.outputVar || '').trim()
        this.dirty = false

        const failed = deps.length ? await this.cascadeRename(deps, oldCode, newCode) : []
        if (failed.length) {
          this.showSnack(
            `บันทึกสูตรแล้ว แต่แก้สูตรที่เรียกไม่สำเร็จ ${failed.length} ตัว: ${failed.join(', ')} — สูตรเหล่านี้ยังชี้ไปที่ ${oldCode} และจะขึ้น ! ต้องแก้เอง`,
            'error',
          )
          return
        }
        this.showSnack(
          deps.length ? `บันทึกสูตรสำเร็จ · แก้สูตรที่เรียกให้แล้ว ${deps.length} ตัว` : 'บันทึกสูตรสำเร็จ',
          'success',
        )
        this.$router.push(this.listRoute())
      } catch (err) {
        this.showSnack((err && err.message) || 'บันทึกไม่สำเร็จ', 'error')
      } finally {
        this.saving = false
      }
    },
    /** @returns {string[]} the OutputVars that could NOT be repointed — never silently dropped. */
    async cascadeRename(deps, oldCode, newCode) {
      const failed = []
      for (const dep of deps) {
        try {
          const rewritten = rewriteRefs(dep, oldCode, newCode)
          if (!rewritten) continue
          await taxCalculateApi.updateFormula(dep.formulaID, taxCalculateStore.buildFormulaRequest({
            name: dep.name,
            expression: rewritten.expression,
            expressionGraphJson: rewritten.graph,
            outputVar: dep.outputVar,
            evalOrder: dep.evalOrder,
            notes: dep.notes,
            isActive: dep.isActive !== false,
          }, dep.evalOrder))
        } catch (_err) {
          failed.push(dep.outputVar)
        }
      }
      return failed
    },

    // ── nav ──
    listRoute() {
      return `/TaxCalculate/${this.configVersionId}/${this.calcVersionId}`
    },
    goBack() {
      this.$router.push(this.listRoute())
    },
    cancelLeave() {
      this.showLeaveDialog = false
      if (this.pendingRoute) { this.pendingRoute(false); this.pendingRoute = null }
    },
    confirmLeave() {
      this.showLeaveDialog = false
      this.dirty = false
      if (this.pendingRoute) { const go = this.pendingRoute; this.pendingRoute = null; go() }
    },
    updateLayoutInsets() {
      const root = this.$refs.tfbPageRoot
      if (!root) return
      const naturalTop = root.getBoundingClientRect().top
      let breadcrumbBottom = naturalTop
      const bc = document.querySelector('.fixed-breadcrumbs')
      if (bc) {
        const r = bc.getBoundingClientRect()
        if (r.height > 0) breadcrumbBottom = r.bottom
      }
      this.topPad = Math.max(0, Math.round(breadcrumbBottom - naturalTop))
      this.pageHeight = Math.max(0, Math.round(window.innerHeight - naturalTop))
      this.$nextTick(() => {
        if (this._ws) { try { Blockly.svgResize(this._ws) } catch (_e) { /* ignore */ } }
      })
    },
    showSnack(message, color) {
      this.snackbar = { show: true, color: color || 'success', message }
    },
  },
}
</script>

<style scoped>
.tfb-page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}
.tfb-title { font-weight: 600; }
.tfb-ov {
  background: rgba(255, 255, 255, 0.18);
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 0.9rem;
  margin-left: 4px;
}
.tfb-sub { opacity: 0.85; font-weight: 400; font-size: 0.9rem; margin-left: 4px; }
.tfb-text-primary { color: rgb(var(--v-theme-primary)) !important; }

.tfb-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.tfb-meta {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
  flex-wrap: wrap;
}
.tfb-meta-name { max-width: 280px; }
.tfb-meta-ov { max-width: 240px; }
.tfb-meta-notes { flex: 1; min-width: 180px; }

.tfb-rename-codes {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.tfb-rename-codes code {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.95rem;
}
.tfb-rename-old {
  background: rgba(0, 0, 0, 0.06);
  text-decoration: line-through;
  opacity: 0.75;
}
.tfb-rename-new { background: rgba(25, 118, 210, 0.14); }
.tfb-rename-deps {
  margin: 0;
  padding-left: 20px;
}
.tfb-rename-deps code {
  font-size: 0.8rem;
  opacity: 0.7;
  margin-left: 4px;
}

.tfb-body { flex: 1; min-height: 0; display: flex; }
.tfb-workspace { flex: 1; min-height: 0; }

.tfb-compiled-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}
.tfb-compiled-main { display: flex; align-items: baseline; gap: 8px; flex: 1; min-width: 0; }
.tfb-compiled-label { font-weight: 700; color: rgb(var(--v-theme-primary)); white-space: nowrap; }
.tfb-compiled-expr {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.9rem;
  overflow-x: auto;
  white-space: nowrap;
}
.tfb-compiled-meta { display: flex; align-items: center; gap: 10px; }
.tfb-charcount { font-size: 0.76rem; color: rgba(var(--v-theme-on-surface), 0.6); }
.tfb-over { color: rgb(var(--v-theme-error)); font-weight: 700; }

/* TEXT ⇄ BLOCKS dialog */
.tfb-paste-input :deep(textarea) {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
}
.tfb-paste-preview {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(var(--v-theme-primary), 0.06);
  overflow-x: auto;
}
.tfb-paste-line { font-size: 0.82rem; line-height: 1.6; }
.tfb-paste-ident {
  background: rgba(var(--v-theme-on-surface), 0.08);
  padding: 1px 6px;
  border-radius: 5px;
  font-size: 0.82rem;
  margin: 0 2px;
}

.tfb-error-list {
  max-height: 88px;
  overflow-y: auto;
  padding: 6px 16px 10px;
  background: rgb(var(--v-theme-surface));
}
.tfb-error-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  color: rgb(var(--v-theme-error));
}
</style>
